import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ColDef,
  GridApi,
  GridReadyEvent,
  RowClassRules,
  ValueFormatterParams,
} from 'ag-grid-community';
import { LopAgGrid } from '../../components/LopAgGrid';
import { LopGridCustomizationDialog } from '../../components/LopGridCustomizationDialog';
import {
  LopHistoryDateFilter,
  isoToday,
} from '../../components/LopHistoryDateFilter';
import { useGridCustomization } from '../../hooks/useGridCustomization';
import { LopGridToolbar, LopGridStatusBar } from '../../components/LopGridToolbar';
import { exportGridCsv } from '../../utils/exportGridCsv';
import { useGridContextMenu } from '../../hooks/useGridContextMenu';
import type { UserCustomizationSettings } from '../../types/UserCustomization';
import {
  applyCustomization,
  buildZebraRule,
  computeSummaryRow,
} from '../../utils/applyCustomization';
import { useFunds } from '../../hooks/useFunds';
import { getFundDataHistory } from '../../api/fundDataHistory';
import type { FundDataHistory } from '../../types/FundDataHistory';
import { t } from '../../i18n/t';
import './FundDataHistoryPage.css';
import { showErrorToast, describeError } from '../../components/ErrorToast';
import { formatDateOnly, formatDateTimeIL } from '../../utils/dateTime';

const RESOURCE_CODE = 'fundDataHistory';

// "שערי קרנות היסטוריים" — read-only viewer of dbo.FundDataHistory_viewHis
// (the valuation engine's per-fund-per-day core table; FDH). NOT a
// LopCrudPage: there is no Edit permission for this resource for any role
// (the table is engine-written), and the isFilled=1 "fill row" rows need a
// muted row style that LopCrudPage doesn't expose a hook for — so this
// mirrors HoldingsPage's custom-grid recipe (fund picker + LopAgGrid direct +
// useGridContextMenu + useUserCustomization) rather than AssistantLogPage's
// LopCrudPage-with-hidden-toolbar recipe.
//
// Fund selection is a plain <select> (not the LopSearchModal magnifier
// Holdings uses) — fundId is REQUIRED here (no "all funds" option), so a
// simple combo showing "<fundId> - <fundName>" is enough. Selecting a fund
// fetches immediately (Inventory's acc-select recipe: new getRows-equivalent
// identity -> refetch). Date range reuses the standard LopHistoryDateFilter
// draft/active + Fetch button recipe (default: last 30 days), same as every
// other history screen in the repo.
function iso30DaysAgo(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

// DATE / DATETIME2 ISO string -> yyyy-mm-dd (date part only). Read-only
// display — same pattern as InventoryPage/HoldingsPage's fmtDate.
function fmtDate(p: ValueFormatterParams<FundDataHistory>): string {
  return formatDateOnly(p.value);
}

// DATETIME2 -> "yyyy-mm-dd hh:mm:ss" — same pattern as AssistantLogPage /
// PublishedFundRatesPage's fmtDateTime.
function fmtDateTime(p: ValueFormatterParams<FundDataHistory>): string {
  return formatDateTimeIL(p.value, { seconds: true });
}

const columnDefs: ColDef<FundDataHistory>[] = [
  { field: 'dDate', headerName: t('fundDataHistory.columns.dDate'), width: 120, editable: false, valueFormatter: fmtDate },
  { field: 'isFilled', headerName: t('fundDataHistory.columns.isFilled'), width: 110, editable: false, cellDataType: 'boolean' },
  { field: 'finalRate', headerName: t('fundDataHistory.columns.finalRate'), width: 130, editable: false, cellDataType: 'number' },
  { field: 'netRate', headerName: t('fundDataHistory.columns.netRate'), width: 130, editable: false, cellDataType: 'number' },
  { field: 'grossRate', headerName: t('fundDataHistory.columns.grossRate'), width: 130, editable: false, cellDataType: 'number' },
  { field: 'unitPrice', headerName: t('fundDataHistory.columns.unitPrice'), width: 130, editable: false, cellDataType: 'number' },
  { field: 'redemptionPrice', headerName: t('fundDataHistory.columns.redemptionPrice'), width: 130, editable: false, cellDataType: 'number' },
  { field: 'grossAssetsValue', headerName: t('fundDataHistory.columns.grossAssetsValue'), width: 150, editable: false, cellDataType: 'number' },
  { field: 'netAssetsValue', headerName: t('fundDataHistory.columns.netAssetsValue'), width: 150, editable: false, cellDataType: 'number' },
  { field: 'publicUnits', headerName: t('fundDataHistory.columns.publicUnits'), width: 150, editable: false, cellDataType: 'number' },
  { field: 'dailyTrackingError', headerName: t('fundDataHistory.columns.dailyTrackingError'), width: 140, editable: false, cellDataType: 'number' },
  { field: 'totalDmf', headerName: t('fundDataHistory.columns.totalDmf'), width: 140, editable: false, cellDataType: 'number' },
  { field: 'securityStrip', headerName: t('fundDataHistory.columns.securityStrip'), width: 130, editable: false, cellDataType: 'number' },
  { field: 'navByCrcy', headerName: t('fundDataHistory.columns.navByCrcy'), width: 130, editable: false, cellDataType: 'number' },
  { field: 'originDate', headerName: t('fundDataHistory.columns.originDate'), width: 120, editable: false, valueFormatter: fmtDate },
  { field: 'updatedAt', headerName: t('fundDataHistory.columns.updatedAt'), width: 160, editable: false, valueFormatter: fmtDateTime },
];

export function FundDataHistoryPage() {
  const { data: funds } = useFunds();

  const [fundId, setFundId] = useState<number | null>(null);
  const [draftFrom, setDraftFrom] = useState<string>(iso30DaysAgo);
  const [draftTo, setDraftTo] = useState<string>(isoToday);
  const [activeFrom, setActiveFrom] = useState<string>(draftFrom);
  const [activeTo, setActiveTo] = useState<string>(draftTo);
  // No data is loaded until the first click on "Fetch" (selecting a fund alone does not load).
  const [fetched, setFetched] = useState<boolean>(false);

  const [rows, setRows] = useState<FundDataHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [customizationOpen, setCustomizationOpen] = useState(false);
  const [dialogSampleRow, setDialogSampleRow] = useState<Record<string, unknown> | null>(null);
  const [dialogInitialField, setDialogInitialField] = useState<string | null>(null);
  const [filterVersion, setFilterVersion] = useState(0);
  const [dialogCurrent, setDialogCurrent] = useState<UserCustomizationSettings | null>(null);
  // Standard grid toolbar state (search box + row counts for the status bar).
  const [quickFilter, setQuickFilter] = useState('');
  const [displayedRowCount, setDisplayedRowCount] = useState(0);

  const gridApiRef = useRef<GridApi<FundDataHistory> | null>(null);

  // Explicit-save customization model (v0.1.84) — see useGridCustomization.
  const {
    customization,
    dirty: customizationDirty,
    toastVisible,
    onGridReady: onCustomizationGridReady,
    onColumnMoved,
    onColumnResized,
    onSortChanged,
    snapshot: snapshotCustomization,
    applyDraft: applyCustomizationDraft,
    quickCustomize,
    reset: resetCustomization,
    saveCustomizationNow,
  } = useGridCustomization(RESOURCE_CODE, gridApiRef);

  // Fetch whenever fundId/activeFrom/activeTo changes. All 3 are required by
  // the API — no fundId means no fetch (a fund must be picked first).
  useEffect(() => {
    if (fundId == null || !fetched) {
      setRows([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFundDataHistory(fundId, activeFrom, activeTo)
      .then((data) => {
        if (cancelled) return;
        setRows(data);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        showErrorToast(e, { title: t('errorToast.titleLoad') });
        setError(t('fundDataHistory.errorPrefix', { message: describeError(e).body }));
        setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fundId, fetched, activeFrom, activeTo]);

  const handleFetch = useCallback(() => {
    setActiveFrom(draftFrom);
    setActiveTo(draftTo);
    setFetched(true);
  }, [draftFrom, draftTo]);

  const effectiveColumnDefs = useMemo<ColDef<FundDataHistory>[]>(
    () => applyCustomization(columnDefs, customization),
    [customization],
  );

  const pinnedBottomRowData = useMemo<Record<string, unknown>[] | undefined>(() => {
    const api = gridApiRef.current;
    if (!api) return undefined;
    const visibleRows: FundDataHistory[] = [];
    api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) visibleRows.push(node.data);
    });
    const summary = computeSummaryRow(visibleRows, customization, columnDefs);
    return summary ? [summary] : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterVersion, customization, rows]);

  // Zebra striping (if enabled) + a muted style for isFilled (fill) rows —
  // these are engine-generated fill-in rows on non-valuation days, not real
  // valuations, so they're visually de-emphasized (opacity, italic date).
  const rowClassRules = useMemo<RowClassRules<FundDataHistory>>(() => {
    const base: RowClassRules<FundDataHistory> = {
      'fund-data-history-page__row--filled': (p) => p.data?.isFilled === true,
    };
    const zebra = buildZebraRule(customization);
    if (zebra) Object.assign(base, zebra);
    return base;
  }, [customization]);

  const handleCustomize = useCallback(
    (sampleRow: Record<string, unknown> | null, colId: string | null) => {
      setDialogCurrent(snapshotCustomization());
      setDialogSampleRow(sampleRow);
      setDialogInitialField(colId);
      setCustomizationOpen(true);
    },
    [snapshotCustomization],
  );

  // Read-only screen -> no onPaste; the hook omits the Paste menu item.
  // Right-click "עיצוב מהיר": persisted per-column shortcut.
  const getColumnCustomization = useCallback(
    (colId: string) => customization?.columns[colId],
    [customization],
  );
  const handleQuickCustomize = quickCustomize;

  const { onCellContextMenu, contextMenu, getRowStyle, clearRowColors } = useGridContextMenu<FundDataHistory>({
    gridApiRef,
    columnDefs,
    resourceCode: RESOURCE_CODE,
    titleKey: 'fundDataHistory.pageTitle',
    onCustomize: handleCustomize,
    onQuickCustomize: handleQuickCustomize,
    getColumnCustomization,
  });

  // Manual row colors are ephemeral: wipe them whenever the data is reloaded
  // (initial load, filter change, auto-refresh — every path goes via setRows).
  useEffect(() => {
    clearRowColors();
  }, [rows, clearRowColors]);

  const handleGridReady = useCallback(
    (event: GridReadyEvent<FundDataHistory>) => {
      gridApiRef.current = event.api;
      onCustomizationGridReady(event);
    },
    [onCustomizationGridReady],
  );

  const handleModelUpdated = useCallback(() => {
    setFilterVersion((v) => v + 1);
    if (gridApiRef.current) setDisplayedRowCount(gridApiRef.current.getDisplayedRowCount());
  }, []);

  const handleExportCsv = useCallback(() => {
    exportGridCsv(gridApiRef.current, RESOURCE_CODE);
  }, []);

  const handleSaveCustomization = useCallback(() => {
    return saveCustomizationNow();
  }, [saveCustomizationNow]);

  const handleDialogClose = useCallback(() => {
    setCustomizationOpen(false);
    setDialogCurrent(null);
    setDialogSampleRow(null);
    setDialogInitialField(null);
  }, []);

  const getRowId = useCallback(
    (p: { data: FundDataHistory }) => `${p.data.fundId}|${p.data.dDate}`,
    [],
  );

  return (
    <div className="fund-data-history-page">
      <h1 className="fund-data-history-page__title">{t('fundDataHistory.pageTitle')}</h1>

      <div className="fund-data-history-page__filter-bar" dir="rtl">
        <label className="fund-data-history-page__filter-label" htmlFor="fdh-fund-select">
          {t('fundDataHistory.fundLabel')}
        </label>
        <select
          id="fdh-fund-select"
          className="fund-data-history-page__fund-select"
          value={fundId ?? ''}
          onChange={(e) => setFundId(e.target.value === '' ? null : Number(e.target.value))}
        >
          <option value="">{t('fundDataHistory.selectFundPlaceholder')}</option>
          {(funds ?? []).map((f) => (
            <option key={f.fundId} value={f.fundId}>
              {`${f.fundId} - ${f.fundName}`}
            </option>
          ))}
        </select>

        <LopHistoryDateFilter
          fromDate={draftFrom}
          toDate={draftTo}
          onFromChange={setDraftFrom}
          onToChange={setDraftTo}
          onApply={handleFetch}
          loading={loading}
        />

        <div className="fund-data-history-page__toolbar">
          {/* Standard grid toolbar (same as LopCrudPage). Read-only screen:
              the edit buttons are shown but disabled. */}
          <LopGridToolbar
            quickFilter={quickFilter}
            onQuickFilterChange={setQuickFilter}
            readOnly
            exportDisabled={fundId == null || !fetched}
            onExport={handleExportCsv}
          />
        </div>
      </div>

      {error && <div className="fund-data-history-page__error">{error}</div>}

      <div className="fund-data-history-page__grid">
        {fundId == null ? (
          <div className="fund-data-history-page__empty">
            {t('fundDataHistory.noFundSelected')}
          </div>
        ) : !fetched ? (
          <div className="fund-data-history-page__empty">
            {t('common.awaitingFetchHint', { fetch: t('common.fetch') })}
          </div>
        ) : (
          <LopAgGrid<FundDataHistory>
            rowData={rows}
            columnDefs={effectiveColumnDefs}
            getRowId={getRowId}
            loading={loading}
            rowClassRules={rowClassRules}
            getRowStyle={getRowStyle}
            pinnedBottomRowData={pinnedBottomRowData}
            onGridReady={handleGridReady}
            onCellContextMenu={onCellContextMenu}
            onModelUpdated={handleModelUpdated}
            onColumnMoved={onColumnMoved}
            onColumnResized={onColumnResized}
            onSortChanged={onSortChanged}
            quickFilterText={quickFilter}
            preventDefaultOnContextMenu={true}
          />
        )}
      </div>

      <LopGridStatusBar
        totalCount={rows.length}
        displayedCount={displayedRowCount}
        isFiltered={quickFilter.length > 0 && displayedRowCount < rows.length}
        customizationDirty={customizationDirty}
        onSaveCustomization={handleSaveCustomization}
        customizationToastVisible={toastVisible}
      />

      {contextMenu}

      {customizationOpen && (
        <LopGridCustomizationDialog
          resourceCode={RESOURCE_CODE}
          baseColumnDefs={columnDefs as ColDef<any>[]}
          current={dialogCurrent}
          sampleRowData={dialogSampleRow}
          initialSelectedField={dialogInitialField}
          onSave={applyCustomizationDraft}
          onReset={resetCustomization}
          onClose={handleDialogClose}
        />
      )}
    </div>
  );
}
